import { useCallback, useMemo } from "react";

import { CallState } from "@b2bportal/core-types";

import type {
  FintechMarketplaceConfig,
  UseInitProducts,
} from "../FintechMarketplaceConfig";

export function useFintechMarketplace(config: FintechMarketplaceConfig) {
  const { products, productsOrder, canContinue, onContinue } = config;

  const [productOffers, fetchProductOffers, productsSelection] =
    useFintechMarketplaceInitProductOffers(config);

  const isProductOffersFetching = useMemo(
    () =>
      Object.values(productOffers).some(
        ({ state }) =>
          state === CallState.InProcess || state === CallState.NotCalled
      ),
    [productOffers]
  );

  return useMemo(
    () => ({
      products,
      productsOrder,
      canContinue,
      onContinue,
      productOffers,
      fetchProductOffers,
      isProductOffersFetching,
      productsSelection,
    }),
    [
      products,
      productsOrder,
      canContinue,
      onContinue,
      productOffers,
      fetchProductOffers,
      isProductOffersFetching,
      productsSelection,
    ]
  );
}

export function useFintechMarketplaceInitProductOffers(config: {
  useInitProducts: UseInitProducts<string>;
}) {
  const { useInitProducts } = config;

  const {
    productOffers,
    fetchProductOffers: fetchProductOffersPerProduct,
    productsSelection,
  } = useInitProducts();

  const fetchProductOffers = useCallback(
    (productKeys?: Set<string>) => {
      const entries = Object.entries(fetchProductOffersPerProduct);
      const filtered = productKeys
        ? entries.filter(([key]) => productKeys.has(key))
        : entries;
      filtered.forEach(([, fn]) => fn());
    },
    [fetchProductOffersPerProduct]
  );

  return [productOffers, fetchProductOffers, productsSelection] as const;
}
